import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import { GridActionsCellItem, GridColumns, GridRowId } from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';

import { CustomDataTable, SETTING_PANEL_MENU_OPTIONS } from 'components/DatatableComponent';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { ModalConfirmationComponent } from 'components/ModalComponent/ModalConfirmationComponent';
import { DASHBOARD_VIEW_MODE } from 'constant/DashboardTypeConstant';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { useDeleteKpiDefinition, useGetKpiDefinitions } from 'services/v1/SystemTenant/AROChart/KpiDefinitionService';
import { RootState } from 'store';
import { KpiDefinition } from 'types/api/SystemTenant/AROChart/KpiDefinitionTypes';

export default function KpiDefinitionListPage() {
  const navigate = useNavigate();
  const { dashboardViewMode } = useSelector((state: RootState) => state.auth);
  const [deleteTarget, setDeleteTarget] = useState<KpiDefinition | null>(null);

  const { data: kpiDefinitions, isLoading, isError, error } = useGetKpiDefinitions();
  const { mutate: deleteDefinition, isLoading: isDeleting } = useDeleteKpiDefinition();

  const isBuilderViewOnly = dashboardViewMode === DASHBOARD_VIEW_MODE.VIEW_ONLY;

  const handleEditClick = useCallback(
    (id: GridRowId) => () => {
      navigate(PATH_CONSTANT.SYSTEM.CHART.EDIT_KPI_DEFINITION.replace(':kpiDefinitionId', id.toString()));
    },
    [navigate]
  );

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    deleteDefinition(deleteTarget.id, {
      onSuccess: () => {
        toast.success('KPI definition deleted successfully');
        setDeleteTarget(null);
      },
    });
  };

  const columns = useMemo<GridColumns>(() => {
    const definitionColumns: GridColumns = [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 260,
        flex: 1,
      },
      {
        field: 'insightView',
        headerName: 'Insight View',
        minWidth: 260,
        flex: 1,
        valueGetter: ({ row }) => row.insightView?.label ?? '',
      },
      {
        field: 'accountabilityDefinition',
        headerName: 'Accountability Definition',
        minWidth: 240,
        flex: 1,
        valueGetter: ({ row }) => row.accountabilityDefinition?.label ?? '',
      },
      {
        field: 'calculationColumns',
        headerName: 'Calculation Columns',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        valueGetter: ({ row }) => row.calculationColumns?.length ?? 0,
      },
      {
        field: 'primaryColumn',
        headerName: 'Primary Attribute',
        minWidth: 220,
        flex: 1,
        valueGetter: ({ row }) =>
          row.attributes?.find((attribute: { primary: boolean }) => attribute.primary)?.displayName ?? '',
      },
      {
        field: 'attributes',
        headerName: 'Attributes',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        valueGetter: ({ row }) => row.attributes?.length ?? 0,
      },
    ];

    if (isBuilderViewOnly) return definitionColumns;

    return [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: ({ id, row }) => [
          // @ts-ignore MUI X v5 action items are valid JSX elements but its tuple typing is too narrow.
          <GridActionsCellItem
            icon={
              <Tooltip title='Edit'>
                <EditIcon />
              </Tooltip>
            }
            key={`edit-${id}`}
            label='Edit'
            onClick={handleEditClick(id)}
          />,
          // @ts-ignore MUI X v5 action items are valid JSX elements but its tuple typing is too narrow.
          <GridActionsCellItem
            disabled={isDeleting}
            icon={
              <Tooltip title='Delete'>
                <DeleteOutlineIcon />
              </Tooltip>
            }
            key={`delete-${id}`}
            label='Delete'
            onClick={() => setDeleteTarget(row as KpiDefinition)}
          />,
        ],
      },
      ...definitionColumns,
    ];
  }, [handleEditClick, isBuilderViewOnly, isDeleting]);

  return (
    <MainLayoutComponent pageTitle='KPI Definition' breadcrumbs={['Dashboard', 'KPI Definition']}>
      <CustomDataTable
        loading={isLoading}
        isError={isError}
        errorData={error ?? undefined}
        rows={kpiDefinitions?.data ?? []}
        columns={columns}
        rowCount={kpiDefinitions?.data?.length ?? 0}
        pageSize={20}
        rowsPerPageOptions={[5, 10, 20]}
        checkboxSelection={false}
        disableSelectionOnClick
        paginationMode='client'
        sortingMode='client'
        filterMode='client'
        getRowId={(row) => row.id}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        experimentalFeatures={{ newEditingApi: true }}
        componentsProps={{
          toolbar: {
            disabled: isLoading || isBuilderViewOnly,
            menuOptions: [SETTING_PANEL_MENU_OPTIONS.FILTER, SETTING_PANEL_MENU_OPTIONS.DENSITY],
            config: {
              onInsertClick: () => navigate(PATH_CONSTANT.SYSTEM.CHART.CREATE_KPI_DEFINITION),
              fieldFocusOnInsert: '',
              insertButtonText: 'Add KPI Definition',
              withExport: false,
            },
          },
        }}
      />

      <ModalConfirmationComponent
        visible={Boolean(deleteTarget)}
        title='Delete KPI Definition'
        message={`Delete "${deleteTarget?.name ?? ''}"? This action cannot be undone.`}
        messageType='warning'
        labelButtonApprove='Delete'
        labelButtonCancel='Cancel'
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onCancel={() => !isDeleting && setDeleteTarget(null)}
        onApprove={handleConfirmDelete}
      />
    </MainLayoutComponent>
  );
}
